<script setup lang="ts">
import { computed, ref } from "vue";
import dayjs from "dayjs";
import { useI18n } from "vue-i18n";
import { formatNumber } from "chartjs-graph";
import LocaleNumberInput from "@/ui/components/LocaleNumberInput.vue";
import { useBasePriceBrutoStore } from "@/modules/base_price/brutoStore";
import { assetsOf } from "@/modules/base_price/utils/brutoCpm";
import ResolvedBrutoCpmDTO = Modules.BasePrice.Data.Resolved.ResolvedBrutoCpmDTO;

const { t } = useI18n();
const store = useBasePriceBrutoStore();

const months = Array.from({ length: 12 }, (_, i) =>
	dayjs().month(i).format("MMM")
);

const isScreen = computed(() => store.selectedAsset !== null);

const title = computed(
	() => store.selectedAsset?.name ?? store.selectedNode?.name ?? ""
);

/** Root first, ending with the level this target hangs off. */
const trail = computed(() => {
	if (!store.selectedNode) return [];

	const ancestors = store.ancestorsOf(store.selectedNode.hierarchy_node_id);

	return isScreen.value ? [...ancestors, store.selectedNode] : ancestors;
});

const cpm = computed<ResolvedBrutoCpmDTO | null>(() => store.selectedCpm);

const hasOwnMonths = computed(
	() => cpm.value?.monthly_cpm_own.some(Boolean) ?? false
);

/** The figure this month would have if it were cleared here — what the field shows greyed out. */
const inheritedLabel = (month: number): string => {
	const value = cpm.value?.monthly_cpm[month] ?? null;

	return value === null ? "—" : formatNumber(value, "money");
};

const sourceLabel = (month: number): string => {
	const sourceId = cpm.value?.monthly_cpm_source_node_id[month] ?? null;
	const source = sourceId === null ? null : store.nodeById(sourceId);

	return source
		? t("base_price.bruto.panel.inherited_from", { name: source.name })
		: t("base_price.bruto.panel.not_set");
};

const ownValue = (month: number): number | null =>
	cpm.value?.monthly_cpm_own[month] ? cpm.value.monthly_cpm[month] : null;

const setMonth = (month: number, value: number | null) =>
	store.setMonth(month, value === null ? null : Math.max(0, value));

const allMonthsValue = ref<number | null>(null);

const applyToAllMonths = () => {
	if (allMonthsValue.value === null) return;

	store.setAllMonths(Math.max(0, allMonthsValue.value));
	allMonthsValue.value = null;
};

/** The level's child nodes and its own screens, with what each one ends up at per month. */
const below = computed(() => {
	const node = store.selectedNode;
	if (!node || isScreen.value) return [];

	return [
		...store.childrenOf(node.hierarchy_node_id).map((child) => ({
			key: `node-${child.hierarchy_node_id}`,
			name: child.name,
			isScreen: false,
			cpm: child.cpm,
			select: () => store.selectNode(child.hierarchy_node_id),
		})),
		...assetsOf(node).map((asset) => ({
			key: `asset-${asset.asset_id}`,
			name: asset.name,
			isScreen: true,
			cpm: asset.cpm,
			select: () => store.selectAsset(node.hierarchy_node_id, asset.asset_id),
		})),
	];
});

const cell = (value: number | null): string =>
	value === null ? "—" : formatNumber(value, "money");
</script>

<template>
	<perfect-scrollbar class="h-100" :options="{ suppressScrollX: true }">
		<div v-if="cpm" class="pa-4">
			<div class="d-flex align-start ga-4 mb-4 flex-wrap">
				<div class="flex-grow-1" style="min-width: 0">
					<div
						v-if="trail.length"
						class="text-caption text-medium-emphasis text-truncate mb-1"
					>
						{{ trail.map((node) => node.name).join(" / ") }}
					</div>
					<div class="d-flex align-center ga-2">
						<!--
							Conditional Font Awesome icons sit inside a span that carries the
							condition: Font Awesome swaps the <i> for an <svg> after it is
							rendered, so Vue must never insert or remove relative to the <i>
							itself — see PlanHierarchyRow.vue.
						-->
						<span v-if="isScreen" class="text-medium-emphasis">
							<i class="fa-regular fa-display" />
						</span>
						<span class="text-h6 font-weight-bold text-truncate">{{
							title
						}}</span>
						<v-chip v-if="isScreen" size="x-small">
							{{ t("base_price.bruto.panel.screen") }}
						</v-chip>
					</div>
				</div>

				<div class="d-flex align-center ga-2">
					<div style="width: 140px">
						<locale-number-input
							v-model="allMonthsValue"
							format="money"
							allow-empty
							size="compact"
							density="compact"
							hide-details
							:placeholder="t('base_price.bruto.panel.set_all')"
							@keyup.enter="applyToAllMonths"
						/>
					</div>
					<v-btn
						variant="tonal"
						:disabled="allMonthsValue === null"
						@click="applyToAllMonths"
					>
						{{ t("base_price.bruto.panel.apply") }}
					</v-btn>
					<v-btn
						v-if="hasOwnMonths"
						variant="text"
						color="secondary"
						prepend-icon="fa-light fa-arrow-rotate-left"
						@click="store.resetToInherited()"
					>
						{{ t("base_price.bruto.panel.reset") }}
					</v-btn>
				</div>
			</div>

			<div class="text-subtitle-2 font-weight-bold mb-1">
				{{ t("base_price.bruto.panel.title") }}
			</div>
			<div class="text-caption text-medium-emphasis mb-4">
				{{ t("base_price.bruto.panel.description") }}
			</div>

			<div class="month-grid mb-6">
				<div
					v-for="(label, i) in months"
					:key="label"
					class="month-card pa-2"
					:class="{ 'month-card--own': cpm.monthly_cpm_own[i] }"
				>
					<div class="d-flex align-center mb-1">
						<span class="text-caption font-weight-bold text-uppercase">{{
							label
						}}</span>
						<v-spacer />
						<v-btn
							v-if="cpm.monthly_cpm_own[i]"
							icon="fa-light fa-xmark"
							size="x-small"
							variant="text"
							density="comfortable"
							v-tooltip="t('base_price.bruto.panel.clear_month')"
							@click="setMonth(i, null)"
						/>
					</div>

					<locale-number-input
						:model-value="ownValue(i) as number"
						format="money"
						allow-empty
						size="compact"
						density="compact"
						hide-details
						:placeholder="inheritedLabel(i)"
						@update:model-value="(value: number | null) => setMonth(i, value)"
					/>

					<div
						class="month-card__source text-caption mt-1 text-truncate"
						:class="
							cpm.monthly_cpm_own[i] ? 'text-warning' : 'text-medium-emphasis'
						"
						:title="
							cpm.monthly_cpm_own[i]
								? t('base_price.bruto.panel.locked')
								: sourceLabel(i)
						"
					>
						<span v-if="cpm.monthly_cpm_own[i]">
							<span class="mr-1"><i class="fa-solid fa-lock" /></span>
							{{ t("base_price.bruto.panel.locked") }}
						</span>
						<span v-else>{{ sourceLabel(i) }}</span>
					</div>
				</div>
			</div>

			<template v-if="!isScreen">
				<div class="text-subtitle-2 font-weight-bold mb-2">
					{{ t("base_price.bruto.panel.children") }}
				</div>

				<div v-if="!below.length" class="text-caption text-medium-emphasis">
					{{ t("base_price.bruto.panel.no_children") }}
				</div>

				<v-table v-else density="compact" class="below-table">
					<thead>
						<tr>
							<th />
							<th v-for="label in months" :key="label" class="text-right">
								{{ label }}
							</th>
						</tr>
					</thead>
					<tbody>
						<tr
							v-for="row in below"
							:key="row.key"
							class="below-table__row"
							@click="row.select"
						>
							<td class="text-truncate below-table__name">
								<span v-if="row.isScreen" class="text-medium-emphasis mr-1">
									<i class="fa-regular fa-display" />
								</span>
								{{ row.name }}
							</td>
							<td
								v-for="(label, i) in months"
								:key="label"
								class="text-right below-table__value"
								:class="{
									'text-warning font-weight-bold': row.cpm.monthly_cpm_own[i],
								}"
							>
								{{ cell(row.cpm.monthly_cpm[i]) }}
							</td>
						</tr>
					</tbody>
				</v-table>
			</template>
		</div>
	</perfect-scrollbar>
</template>

<style scoped lang="scss">
.month-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
	gap: 8px;
}

.month-card {
	border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
	border-left: 3px solid transparent;
	border-radius: 6px;
	background: rgb(var(--v-theme-surface));
}

// Same mark the tree uses for a value set on the level itself.
.month-card--own {
	border-left-color: rgb(var(--v-theme-warning));
}

.month-card__source {
	min-height: 18px;
}

.below-table__row {
	cursor: pointer;
}

.below-table__name {
	max-width: 220px;
}

.below-table__value {
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
}
</style>
